import React, { useState, useEffect } from 'react';
import { clientAPI } from '../services/api';

interface AllDevicesProps {
  onNavigateToPlatform?: (platform: 'camel' | 'starlink', hexValue: string) => void;
}

interface Device {
  id: string;
  vehicle_id: string;
  serial_number: string;
  imei_number?: string;
  iccid?: string;
  client_id: string;
  client_name?: string;
  company_name?: string;
  sim_provider?: string;
  sim_text_number?: string;
  application?: string;
  model_version?: string;
  device_type?: string;
  hex_value?: string;
  device_manufacturer?: string;
  sim_data_plan?: string;
}

const AllDevices: React.FC<AllDevicesProps> = ({ onNavigateToPlatform }) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [platformModalDevice, setPlatformModalDevice] = useState<Device | null>(null);

  useEffect(() => { fetchAllDevices(); }, []);

  const fetchAllDevices = async () => {
    try {
      setLoading(true);
      const response = await clientAPI.getAll();
      const allDevices: Device[] = [];
      response.data.forEach((client: any) => {
        if (client.devices && Array.isArray(client.devices)) {
          client.devices.forEach((device: any) => {
            allDevices.push({ ...device, client_name: client.name, company_name: client.company_name });
          });
        }
      });
      setDevices(allDevices);
    } catch (error) { console.error('Error fetching devices:', error); }
    finally { setLoading(false); }
  };

  const handleDeleteDevice = async (device: Device) => {
    if (!window.confirm(`Delete device ${device.serial_number}? This cannot be undone.`)) return;
    try {
      await clientAPI.deleteDevice(device.id);
      await fetchAllDevices();
      setSelectedDevice(null);
      alert('Device deleted successfully!');
    } catch (error: any) {
      alert('Error deleting device: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateDevice = async () => {
    if (!editingDevice) return;
    try {
      await clientAPI.updateDevice(editingDevice.client_id, editingDevice.id, {
        serial_number: editingDevice.serial_number,
        imei_number: editingDevice.imei_number || '',
        iccid: editingDevice.iccid || '',
        sim_provider: editingDevice.sim_provider || '',
        sim_text_number: editingDevice.sim_text_number || '',
        vehicle_id: editingDevice.vehicle_id,
        application: editingDevice.application || '',
        model_version: editingDevice.model_version || '',
        device_type: editingDevice.device_type || '',
        hex_value: editingDevice.hex_value || '',
        device_manufacturer: editingDevice.device_manufacturer || '',
        sim_data_plan: editingDevice.sim_data_plan || '',
        fleet: (editingDevice as any).fleet || '',
      });
      await fetchAllDevices();
      setEditingDevice(null);
      alert('Device updated successfully!');
    } catch (error: any) {
      alert('Error updating device: ' + (error.response?.data?.error || error.message));
    }
  };

  const filteredDevices = devices.filter(d =>
    d.vehicle_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyText = (text: string) => { navigator.clipboard.writeText(text); };

  const Field = ({ label, value, mono, copyable }: { label: string; value?: string; mono?: boolean; copyable?: boolean }) => {
    if (!value) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
        <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '90px' }}>{label}</span>
        <span style={{ fontSize: '12px', color: '#1f2937', fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit', textAlign: 'right', wordBreak: 'break-all', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {value}
          {copyable && (
            <button onClick={e => { e.stopPropagation(); copyText(value); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', padding: 0, opacity: 0.5 }} title="Copy">📋</button>
          )}
        </span>
      </div>
    );
  };

  const inputStyle = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', width: '100%', fontSize: '14px', outline: 'none', boxSizing: 'border-box' as const };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: 700, margin: 0, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          📱 All Devices
        </h1>
        <p style={{ color: '#888', margin: '4px 0 0 0', fontSize: '14px' }}>View and manage all devices across all clients</p>
      </div>

      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Total Devices', value: devices.length, icon: '📱', color: '#2563eb' },
          { label: 'Showing', value: filteredDevices.length, icon: '🔍', color: '#7c3aed' },
          { label: 'Clients', value: new Set(devices.map(d => d.client_id)).size, icon: '👤', color: '#059669' },
        ].map((t, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '20px', marginBottom: '4px' }}>{t.icon}</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: t.color }}>{t.value}</div>
            <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search by vehicle ID, serial number, or client..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '10px', outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      {/* Device cards */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#888' }}>Loading devices...</div>
      ) : filteredDevices.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#888' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px', opacity: 0.3 }}>📱</div>
          <div>No devices found</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
          {filteredDevices.map(device => (
            <div
              key={device.id}
              style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden',
                transition: 'box-shadow 0.2s, border-color 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#2563eb'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
            >
              {/* Card header */}
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#1f2937' }}>🚗 {device.vehicle_id || 'N/A'}</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{device.client_name}{device.company_name ? ` · ${device.company_name}` : ''}</div>
                </div>
                {device.sim_provider && (
                  <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: '#f3f4f6', color: '#6b7280' }}>{device.sim_provider}</span>
                )}
              </div>

              {/* Card body */}
              <div style={{ padding: '12px 16px' }}>
                <Field label="Serial" value={device.serial_number} mono copyable />
                <Field label="IMEI" value={device.imei_number} mono copyable />
                <Field label="ICCID" value={device.iccid} mono copyable />
                <Field label="Hex" value={device.hex_value} mono copyable />
                <Field label="SIM Number" value={device.sim_text_number} mono />
                <Field label="Data Plan" value={device.sim_data_plan} />
                <Field label="Application" value={device.application} />
                <Field label="Type" value={device.device_type} />
                <Field label="Manufacturer" value={device.device_manufacturer} />
                <Field label="Model" value={device.model_version} />
              </div>

              {/* Card footer */}
              {device.hex_value && (
                <div style={{ padding: '0 16px 14px' }}>
                  <button
                    onClick={e => { e.stopPropagation(); setPlatformModalDevice(device); }}
                    style={{
                      width: '100%', padding: '8px', background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                      color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    🔍 Search in Platform
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* View Device Modal */}
      {selectedDevice && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Device Details</h3>
              <button onClick={() => setSelectedDevice(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                ['Client', selectedDevice.client_name],
                ['Company', selectedDevice.company_name],
                ['Vehicle ID', selectedDevice.vehicle_id],
                ['Serial Number', selectedDevice.serial_number],
                ['IMEI', selectedDevice.imei_number],
                ['ICCID', selectedDevice.iccid],
                ['SIM Provider', selectedDevice.sim_provider],
                ['Device Type', selectedDevice.device_type],
              ].map(([label, val], i) => (
                <div key={i}>
                  <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ fontSize: '14px', fontWeight: 500, padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>{val || 'N/A'}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button onClick={() => setSelectedDevice(null)} style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Device Modal */}
      {editingDevice && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Edit Device</h3>
              <button onClick={() => setEditingDevice(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                ['Vehicle ID', 'vehicle_id'],
                ['Serial Number', 'serial_number'],
                ['IMEI', 'imei_number'],
                ['ICCID', 'iccid'],
                ['SIM Provider', 'sim_provider'],
                ['Device Type', 'device_type'],
              ].map(([label, key]) => (
                <div key={key}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '12px', color: '#374151' }}>{label}</label>
                  <input
                    type="text"
                    value={(editingDevice as any)[key] || ''}
                    onChange={e => setEditingDevice({ ...editingDevice, [key]: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              ))}
            </div>
            <div style={{ marginTop: '16px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingDevice(null)} style={{ padding: '8px 20px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '13px', color: '#374151' }}>Cancel</button>
              <button onClick={handleUpdateDevice} style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>Update Device</button>
            </div>
          </div>
        </div>
      )}

      {/* Platform Selection Modal */}
      {platformModalDevice && (
        <div onClick={() => setPlatformModalDevice(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', maxWidth: '360px', width: '90%' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 700 }}>Select Platform</h3>
            <p style={{ margin: '0 0 20px 0', color: '#6b7280', fontSize: '13px' }}>
              Search for <strong style={{ fontFamily: 'monospace' }}>{platformModalDevice.hex_value}</strong> in:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={() => { if (onNavigateToPlatform) onNavigateToPlatform('camel', platformModalDevice.hex_value!); setPlatformModalDevice(null); }}
                style={{ padding: '12px', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
              >
                🚀 CAMEL Platform
              </button>
              <button
                onClick={() => { if (onNavigateToPlatform) onNavigateToPlatform('starlink', platformModalDevice.hex_value!); setPlatformModalDevice(null); }}
                style={{ padding: '12px', background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
              >
                🛰️ STARLINK Platform
              </button>
              <button
                onClick={() => setPlatformModalDevice(null)}
                style={{ padding: '10px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', marginTop: '4px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllDevices;
